/**
 * WC-X4 — stale-lead hygiene cadence + resolution helpers.
 * Shared by server sync, reminder normalization, and tests.
 */
import {
  normalizeHygieneCadence,
  type LeadFollowupHygieneConfig,
} from "./workplaceMessagesPolicy.js";

export const DEFAULT_LEAD_HYGIENE_CADENCE_DAYS = [3, 7, 14] as const;
export const LEAD_HYGIENE_NUDGE_KIND = "lead_hygiene_nudge";

export type LeadFollowupHygieneResolved = LeadFollowupHygieneConfig & {
  /** Feature off when false. */
  enabled: boolean;
  /** Org stale threshold (days); null when unset. */
  thresholdDays: number | null;
};

export type LeadTouchInput = {
  updatedAt: string;
  createdAt: string;
  sentAt?: string | null;
  followUps?: readonly { status?: string; due_date?: string }[] | null;
};

export type HygieneResolutionAction = "dismiss" | "resolve" | "reassign";

export type HygieneResolutionLog = {
  notification_id: string;
  lead_id: string;
  cadence_days: number;
  action: HygieneResolutionAction;
  reason?: string | null;
  assignee_user_id?: string | null;
  resolved_at: string;
};

/** Parse org JSON into enabled cadence config (Location Tools). */
export function resolveLeadFollowupHygieneFromOrgConfig(
  raw: unknown
): LeadFollowupHygieneResolved {
  const parsed =
    raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;

  let thresholdDays: number | null = null;
  const hyRaw = parsed?.lead_followup_hygiene_days;
  if (typeof hyRaw === "number" && Number.isFinite(hyRaw)) {
    const n = Math.floor(hyRaw);
    if (n >= 1 && n <= 365) thresholdDays = n;
  }

  const cadenceRaw = parsed?.lead_followup_hygiene_cadence_days;
  let promptAfterDays = normalizeHygieneCadence(
    Array.isArray(cadenceRaw) ? (cadenceRaw as number[]) : undefined
  );
  if (promptAfterDays.length === 0 && thresholdDays != null) {
    promptAfterDays = normalizeHygieneCadence(DEFAULT_LEAD_HYGIENE_CADENCE_DAYS);
  }

  const enabled = thresholdDays != null && promptAfterDays.length > 0;
  return { enabled, thresholdDays, promptAfterDays };
}

/** Calendar days since the last meaningful lead touch. */
export function computeDaysSinceLastTouch(input: LeadTouchInput, nowMs = Date.now()): number {
  const candidates: number[] = [];
  for (const iso of [input.updatedAt, input.createdAt, input.sentAt ?? null]) {
    if (typeof iso !== "string" || !iso.trim()) continue;
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms)) candidates.push(ms);
  }
  for (const f of input.followUps ?? []) {
    if (!f || f.status === "open") continue;
    if (typeof f.due_date === "string" && f.due_date.trim()) {
      const ms = Date.parse(f.due_date);
      if (!Number.isNaN(ms)) candidates.push(ms);
    }
  }
  if (candidates.length === 0) return 0;
  const lastMs = Math.max(...candidates);
  return Math.max(0, Math.floor((nowMs - lastMs) / 86_400_000));
}

/** Highest cadence tier crossed (e.g. day 8 → 7 when cadence is 3/7/14). */
export function activeHygieneCadenceTier(
  daysSinceTouch: number,
  cadenceDays: readonly number[]
): number | null {
  const sorted = normalizeHygieneCadence(cadenceDays);
  let active: number | null = null;
  for (const tier of sorted) {
    if (daysSinceTouch >= tier) active = tier;
  }
  return active;
}

/** Next cadence tier after `current`, if any. */
export function nextHygieneCadenceTier(
  currentTier: number,
  cadenceDays: readonly number[]
): number | null {
  const sorted = normalizeHygieneCadence(cadenceDays);
  const idx = sorted.indexOf(currentTier);
  if (idx < 0 || idx >= sorted.length - 1) return null;
  return sorted[idx + 1] ?? null;
}

export function parseHygieneTierFromTitle(title: string): number | null {
  const m = /^Stale lead — (\d+) day/.exec(String(title || "").trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 10);
  return Number.isFinite(n) && n >= 1 && n <= 365 ? n : null;
}

export function buildHygieneNudgeTitle(tierDays: number, leadLabel: string): string {
  const label = leadLabel.trim() || "Lead";
  return `Stale lead — ${tierDays} day${tierDays === 1 ? "" : "s"} (${label})`;
}

export function buildHygieneNudgeBody(tierDays: number, daysSinceTouch: number): string {
  return (
    `No follow-up or disposition recorded for ${daysSinceTouch} day${daysSinceTouch === 1 ? "" : "s"}. ` +
    `Your org prompts at ${tierDays} day${tierDays === 1 ? "" : "s"}. ` +
    `Open the lead to log contact, disposition, or reassign ownership. ` +
    `Dismiss or resolve here when handled — managers see resolution notes on the lead timeline.`
  );
}

/** Tier is satisfied when a prior dismiss/resolve exists before the next tier threshold. */
export function isHygieneTierSatisfied(input: {
  tierDays: number;
  daysSinceTouch: number;
  cadenceDays: readonly number[];
  priorStatuses: readonly { tierDays: number; status: string }[];
}): boolean {
  for (const row of input.priorStatuses) {
    if (row.tierDays !== input.tierDays) continue;
    const s = String(row.status || "").trim().toLowerCase();
    if (s !== "dismissed" && s !== "resolved" && s !== "reassigned") continue;
    const next = nextHygieneCadenceTier(input.tierDays, input.cadenceDays);
    if (next == null) return true;
    if (input.daysSinceTouch < next) return true;
  }
  return false;
}

export function buildHygieneResolutionLeadEventPayload(log: HygieneResolutionLog): Record<string, unknown> {
  return {
    via: "lead_hygiene_nudge",
    notification_id: log.notification_id,
    cadence_days: log.cadence_days,
    action: log.action,
    ...(log.reason?.trim() ? { reason: log.reason.trim().slice(0, 2000) } : {}),
    ...(log.assignee_user_id ? { assignee_user_id: log.assignee_user_id } : {}),
    resolved_at: log.resolved_at,
  };
}
